import type { Coord } from "@dailywars/core/engine";

import type { CanvasAttackFxAnimation } from "#/game/animation/types";

import { attackFxBulletProgress } from "./boardAnimations";
import { TILE_SIZE } from "./boardConstants";

const COMBAT_TRACER_RADIUS = 4;
const COMBAT_TRACER_STROKE = "rgb(20, 20, 20)";
const COMBAT_TRACER_STROKE_WIDTH = 1;
const COMBAT_TRACER_FADE_OUT = 0.4;

function tileCenter(tile: Coord): { x: number; y: number } {
  return {
    x: tile.x * TILE_SIZE + TILE_SIZE / 2,
    y: tile.y * TILE_SIZE + TILE_SIZE / 2,
  };
}

function combatBulletMotion({
  fromTile,
  toTile,
  bulletT,
}: {
  fromTile: Coord;
  toTile: Coord;
  bulletT: number;
}): { x: number; y: number; fadeOut: number } | null {
  const from = tileCenter(fromTile);
  const to = tileCenter(toTile);
  if (from.x === to.x && from.y === to.y) return null;
  if (bulletT <= 0) return null;

  const travelT = Math.min(1, bulletT);
  const eased = 1 - (1 - travelT) ** 2;
  return {
    x: from.x + (to.x - from.x) * eased,
    y: from.y + (to.y - from.y) * eased,
    fadeOut: bulletT > 1 ? Math.max(0, 1 - (bulletT - 1) / COMBAT_TRACER_FADE_OUT) : 1,
  };
}

function drawCombatBulletPulse({
  ctx,
  fromTile,
  toTile,
  color,
  bulletT,
}: {
  ctx: CanvasRenderingContext2D;
  fromTile: Coord;
  toTile: Coord;
  color: string;
  bulletT: number;
}): void {
  const motion = combatBulletMotion({ fromTile, toTile, bulletT });
  if (!motion) return;

  ctx.save();
  ctx.globalAlpha = motion.fadeOut;
  ctx.fillStyle = color;
  ctx.strokeStyle = COMBAT_TRACER_STROKE;
  ctx.lineWidth = COMBAT_TRACER_STROKE_WIDTH;
  ctx.beginPath();
  ctx.arc(motion.x, motion.y, COMBAT_TRACER_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

export function drawAttackFxUnderUnits({
  ctx,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  animation: CanvasAttackFxAnimation;
  nowMs: number;
}): void {
  const bulletT = attackFxBulletProgress(animation, nowMs);
  for (const tracer of animation.tracers) {
    drawCombatBulletPulse({
      ctx,
      fromTile: tracer.from,
      toTile: tracer.to,
      color: tracer.color,
      bulletT,
    });
  }
}
